"use strict";
/**
 * SCALA MALLO & IDC — pannello analisi e suggeritore (@leopart/calc).
 *
 * La scala di Javier Mallo pesa un'esercitazione con palla in % di gara
 * (30/40/60/80/100); il flusso corregge il peso nominale per come il carico
 * viene erogato (Continuo ×1.00 · Intermittente ×0.65 · Analitico ×0.45).
 *
 * Regole condivise con training-context.ts: eleggibile = almeno una categoria
 * col gruppo 'mallo' abilitato, minuti pieni (ADR 028), peso = tempo effettivo
 * quando presente. I blocchi senza classificazione restano validi: esclusi dai
 * rispettivi calcoli e conteggiati (`missingMallo`/`missingFlow`).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.malloIdc = malloIdc;
exports.suggerisciMallo = suggerisciMallo;
const kernel_1 = require("./kernel");
const training_mix_1 = require("./training-mix");
const training_context_1 = require("./training-context");
const training_context_constants_1 = require("./training-context.constants");
/** Il pannello «Mallo & IDC». `null` se nessun blocco eleggibile nel periodo. */
function malloIdc(blocchi, categorieMallo) {
    var _a, _b, _c, _d;
    const eleggibili = (0, training_context_1.conGruppo)(blocchi, categorieMallo);
    if (eleggibili.length === 0)
        return null;
    const perLivello = new Map(training_context_constants_1.MALLO_LEVELS.map((l) => [l.v, 0]));
    const perFlusso = new Map(training_context_constants_1.FLOW_TYPES.map((f) => [f, 0]));
    let senzaPeso = 0;
    let senzaFlusso = 0;
    let ceSomma = 0;
    let minutiConPeso = 0;
    let idcSomma = 0;
    let minutiConPesoEFlusso = 0;
    let minutiCognitivi = 0;
    let minutiMuscolari = 0;
    for (const b of eleggibili) {
        const min = (0, training_context_1.minutiPesati)(b);
        const peso = Number(b.malloWeight) || 0;
        const flusso = b.flowType && training_context_constants_1.FLOW_TYPES.includes(b.flowType) ? b.flowType : null;
        if (peso > 0) {
            perLivello.set(peso, ((_a = perLivello.get(peso)) !== null && _a !== void 0 ? _a : 0) + min);
            ceSomma += peso * min;
            minutiConPeso += min;
            if (peso >= training_context_constants_1.SOGLIA_COGNITIVA)
                minutiCognitivi += min;
            if (peso >= training_context_constants_1.SOGLIA_MUSCOLARE)
                minutiMuscolari += min;
        }
        else {
            senzaPeso += 1;
        }
        if (flusso) {
            perFlusso.set(flusso, ((_b = perFlusso.get(flusso)) !== null && _b !== void 0 ? _b : 0) + min);
            if (peso > 0) {
                idcSomma += peso * min * training_context_constants_1.FLOW_FACTORS[flusso];
                minutiConPesoEFlusso += min;
            }
        }
        else {
            senzaFlusso += 1;
        }
    }
    const continuo = (_c = perFlusso.get('continuo')) !== null && _c !== void 0 ? _c : 0;
    const intermittente = (_d = perFlusso.get('intermittente')) !== null && _d !== void 0 ? _d : 0;
    const ciRatio = intermittente > 0 ? (0, kernel_1.round)(continuo / intermittente, 2) : null;
    return {
        eligibleBlocks: eleggibili.length,
        missingMallo: senzaPeso,
        missingFlow: senzaFlusso,
        ce: minutiConPeso > 0 ? (0, kernel_1.round)(ceSomma / minutiConPeso, 0) : null,
        idc: minutiConPesoEFlusso > 0
            ? (0, kernel_1.round)(idcSomma / minutiConPesoEFlusso, 0)
            : null,
        cognitivePct: (0, training_mix_1.percentuale)(minutiCognitivi, minutiConPeso),
        muscularPct: (0, training_mix_1.percentuale)(minutiMuscolari, minutiConPeso),
        weightedMinutes: minutiConPeso,
        cognitiveMinutes: minutiCognitivi,
        muscularMinutes: minutiMuscolari,
        byLevel: training_context_constants_1.MALLO_LEVELS.map((l) => {
            var _a;
            return ({
                level: l.v,
                minutes: (_a = perLivello.get(l.v)) !== null && _a !== void 0 ? _a : 0,
            });
        }),
        byFlow: training_context_constants_1.FLOW_TYPES.map((f) => { var _a; return ({ flow: f, minutes: (_a = perFlusso.get(f)) !== null && _a !== void 0 ? _a : 0 }); }),
        ciRatio,
        ciAlert: ciRatio != null && ciRatio < training_context_constants_1.CI_ALERT_SOTTO,
    };
}
/**
 * Default per le categorie di sistema quando il titolo non matcha nessuna
 * regola. Le categorie custom abilitate al mallo non hanno un default noto:
 * fallback 60/continuo.
 */
const DEFAULT_PER_CATEGORIA = {
    'partita-a-tema': {
        malloWeight: 80,
        flowType: 'continuo',
        reasonIt: 'default categoria: partita a tema',
    },
    'tattico-situazionale': {
        malloWeight: 60,
        flowType: 'continuo',
        reasonIt: 'default categoria: situazionale',
    },
    tecnico: {
        malloWeight: 30,
        flowType: 'analitico',
        reasonIt: 'default categoria: tecnico',
    },
};
/**
 * Suggerimento automatico peso+flusso da titolo e categorie del blocco.
 * Regex identiche al prototipo, valutate in quest'ordine: le regole sul
 * titolo vincono sempre; senza match, default della categoria eleggibile col
 * peso più alto. `null` se nessuna categoria del blocco è abilitata al mallo.
 */
function suggerisciMallo(categoryIds, title, categorieMallo) {
    var _a;
    const eleggibili = categoryIds.filter((c) => categorieMallo.includes(c));
    if (eleggibili.length === 0)
        return null;
    const t = String(title || '').toLowerCase();
    const partitaLibera = /partita libera|11\s*[cvx]\s*11|amichevole|\bgara\b|tutto campo/.test(t);
    // es. 1+4>6, 1+7>10: situazione organizzata a flusso interrotto
    const situazioneOrganizzata = /\d\s*\+\s*\d+\s*>\s*\d+/.test(t);
    const rondo = /rond|torello/.test(t);
    const jolly = /jolly|\+\s*j\b|\d\s*[cvx]\s*\d\s*\+\s*\d|\d\s*\+\s*\d+\s*[cvx]\s*\d+/.test(t);
    const coppia = t.match(/(\d+)\s*[cvx]\s*(\d+)/);
    const opposizioneRidotta = /costruzion|ression|cross|marcatur|tiro|difesa porta/.test(t);
    const analitico = /analitic|tecnica|dominio|ricezion|rimessa|colpo di testa|trasmission|passagg|conduzion|calciare|appogg/.test(t);
    if (partitaLibera) {
        return { malloWeight: 100, flowType: 'continuo', reasonIt: 'partita libera / spazi ampi' };
    }
    if (situazioneOrganizzata) {
        return { malloWeight: 40, flowType: 'intermittente', reasonIt: 'situazione organizzata a flusso interrotto (N+N>N)' };
    }
    if (rondo) {
        return { malloWeight: 40, flowType: 'analitico', reasonIt: 'rondò: opposizione ridotta, densità decisionale limitata' };
    }
    if (jolly) {
        return { malloWeight: 60, flowType: 'continuo', reasonIt: 'superiorità numerica (jolly)' };
    }
    if (coppia) {
        const a = Number(coppia[1]);
        const c = Number(coppia[2]);
        if (a <= 2 && c <= 2) {
            return { malloWeight: 40, flowType: 'intermittente', reasonIt: `opposizione ridotta (${coppia[0]})` };
        }
        if (a !== c) {
            return { malloWeight: 60, flowType: 'continuo', reasonIt: `superiorità numerica (${coppia[0]})` };
        }
        return { malloWeight: 80, flowType: 'continuo', reasonIt: `SSG in parità (${coppia[0]})` };
    }
    if (opposizioneRidotta) {
        return { malloWeight: 40, flowType: 'intermittente', reasonIt: 'situazione organizzata con fasi distinte' };
    }
    if (analitico) {
        return { malloWeight: 30, flowType: 'analitico', reasonIt: 'tecnica senza opposizione' };
    }
    // Nessuna regola sul titolo: default della categoria col peso più alto.
    let migliore = null;
    for (const slug of eleggibili) {
        const candidato = (_a = DEFAULT_PER_CATEGORIA[slug]) !== null && _a !== void 0 ? _a : {
            malloWeight: 60,
            flowType: 'continuo',
            reasonIt: 'default categoria personalizzata',
        };
        if (!migliore || candidato.malloWeight > migliore.malloWeight) {
            migliore = candidato;
        }
    }
    return migliore;
}
